/**
 * Core organization context: memberships, the active org, RBAC permissions,
 * the onboarding machine + profile, and the org-creation flows (plus the
 * transient trial-capture state).
 *
 * Split out of the legacy monolithic `OrgProvider` (audit item 26). Billing
 * and workspaces live in sibling providers (`BillingProvider`,
 * `WorkspacesProvider`) that nest inside this one and read `activeOrg` from
 * here. The public `useOrg()` adapter in `OrgProvider.tsx` recombines all
 * three contexts so existing call sites keep compiling unchanged.
 *
 * Every exposed value is memoized and every callback is `useCallback`-wrapped
 * so consumers only re-render when the data they read actually changes (the
 * legacy provider rebuilt its context value on every render).
 *
 * The legacy `refreshKey` counter that doubled as an event bus is gone:
 * callers now invoke the explicit `refreshCore()` refetch instead.
 *
 * @module lib/OrgCoreProvider
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSupabase } from './SupabaseProvider';
import { captureException } from './telemetry/posthog/frontend';
import type {
  Organization,
  OrgMember,
  OnboardingState,
  UserOrgMembership,
  SetupStep,
  ActivatedTrial,
} from './org-types';
import { ACTIVE_ORG_STORAGE_KEY } from './org-types';

/**
 * Thrown when the server rejects org creation because the user has reached
 * their organization limit. Maps to HTTP 403 `{error: "org_limit_reached"}`.
 */
export class OrgLimitReachedError extends Error {
  constructor(message = 'Organization limit reached') {
    super(message);
    this.name = 'OrgLimitReachedError';
  }
}

/**
 * Thrown when the server asks the client to retry with a new idempotency key
 * (a prior create attempt produced a conflicting state). Maps to HTTP 409
 * `{error: "create_failed_retry_with_new_key"}`.
 */
export class CreateOrgRetryError extends Error {
  constructor(message = 'Create failed, please retry with a new key') {
    super(message);
    this.name = 'CreateOrgRetryError';
  }
}

/**
 * Generic create-org failure: network error, unexpected status, malformed body.
 * Callers should surface a generic retry message.
 */
export class CreateOrgError extends Error {
  constructor(message = 'Failed to create organization') {
    super(message);
    this.name = 'CreateOrgError';
  }
}

/** Shape of the core organization context value. */
export interface OrgCoreContextType {
  /** Currently active organization, or null if not yet loaded. */
  activeOrg: Organization | null;
  /** Switch the active organization by ID. */
  setActiveOrg: (orgId: string) => void;
  /** All organizations the user belongs to. */
  userOrgs: Organization[];
  /** All memberships with role info. */
  memberships: UserOrgMembership[];
  /** Members of the active org. */
  orgMembers: OrgMember[];
  /** The current user's role name in the active org. */
  userRole: string | null;
  /** Permission keys granted to the active role in the active org. */
  permissions: Set<string>;
  /** Returns whether the active role grants `key`. Fails closed. */
  hasPermission: (key: string) => boolean;
  /** Whether the provider is still loading. */
  isLoading: boolean;
  /** User-facing error message from org initialization, or null. */
  orgError: string | null;
  /** Re-fetch memberships, the active org, billing-independent core data. */
  refreshCore: () => void;
  /** Re-fetch only the active org's member list. No-op when no org is active. */
  refreshMembers: () => Promise<void>;
  /** Current onboarding state. */
  onboardingState: OnboardingState;
  /** Ordered list of setup steps the user must still complete. */
  setupSteps: SetupStep[];
  /** Update the current user's first/last name via Supabase auth. */
  updateProfile: (data: { firstName: string; lastName: string }) => Promise<void>;
  /** Create a new organization via the metering proxy. */
  createOrg: (name: string, idempotencyKey?: string) => Promise<Organization>;
  /** Create an organization plus its default workspace. */
  createOrgWithWorkspace: (
    orgName: string,
    workspaceName: string,
    websiteUrl: string,
    idempotencyKey: string,
  ) => Promise<{ orgId: string; workspaceId: string }>;
  /** Complete the onboarding flow: update profile and create org. */
  completeOnboarding: (data: {
    firstName: string;
    lastName: string;
    orgName: string;
  }) => Promise<void>;
  /** Details of a trial that was just activated by `createOrg`, or null. */
  justActivatedTrial: ActivatedTrial | null;
  /** Clear the just-activated-trial flag (the core half of acknowledgeTrial). */
  clearJustActivatedTrial: () => void;
}

const OrgCoreContext = createContext<OrgCoreContextType | undefined>(undefined);

/** Props for the OrgCoreProvider component. */
interface OrgCoreProviderProps {
  children: React.ReactNode;
}

/**
 * Provides the core organization context (memberships, active org, RBAC,
 * onboarding, org creation). Must be a child of `SupabaseProvider`.
 */
export const OrgCoreProvider: React.FC<OrgCoreProviderProps> = ({ children }) => {
  const { user, supabase, loading: authLoading } = useSupabase();

  const [memberships, setMemberships] = useState<UserOrgMembership[]>([]);
  const [activeOrg, setActiveOrgState] = useState<Organization | null>(null);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [onboardingState, setOnboardingState] = useState<OnboardingState>({
    status: 'none',
  });
  const [justActivatedTrial, setJustActivatedTrial] =
    useState<ActivatedTrial | null>(null);

  /** Ref-based lock to prevent concurrent org creation calls. */
  const creatingRef = useRef(false);

  /** Stable ref for the supabase client to avoid re-triggering effects. */
  const supabaseRef = useRef(supabase);
  supabaseRef.current = supabase;

  /**
   * Latest-wins guards (A11). Each org-scoped fetch stamps the ref with the
   * id it was issued for; a response that lands after the active org/role
   * changed is discarded, so org B can never end up showing org A's members
   * or A's permission set.
   */
  const latestMembersOrgRef = useRef<string | null>(null);
  const latestPermissionsRoleRef = useRef<string | null>(null);

  const userOrgs = useMemo(
    () => memberships.map((m) => m.organizations),
    [memberships],
  );

  /** Membership row for the active org, or undefined. */
  const activeMembership = useMemo(
    () =>
      activeOrg
        ? memberships.find((m) => m.organization_id === activeOrg.id)
        : undefined,
    [activeOrg, memberships],
  );

  /** The user's role name in the active org. */
  const userRole = activeMembership?.org_roles?.name ?? null;

  /** The role UUID used to resolve the active permission set. */
  const activeRoleId = activeMembership?.role_id ?? null;

  /** Switch active org by ID. Persists to localStorage. */
  const setActiveOrg = useCallback(
    (orgId: string) => {
      const org = memberships.find((m) => m.organization_id === orgId)
        ?.organizations;
      if (org) {
        setActiveOrgState(org);
        try {
          localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
        } catch {
          // localStorage unavailable; non-fatal
        }
      }
    },
    [memberships],
  );

  /** Trigger a re-fetch of core org data (memberships + active org). */
  const refreshCore = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  /**
   * Fetch the permission key set for a role via `org_role_permissions` joined
   * with `org_permissions`. Fails closed: an error leaves the permission set
   * empty so every `hasPermission` call denies until we successfully re-fetch.
   */
  const fetchPermissions = useCallback(
    async (roleId: string | null | undefined) => {
      latestPermissionsRoleRef.current = roleId ?? null;
      if (!roleId) {
        setPermissions(new Set());
        return;
      }
      try {
        const { data, error } = await supabaseRef.current
          .from('org_role_permissions')
          .select('org_permissions(key)')
          .eq('role_id', roleId);

        // Drop a stale response: the active role changed mid-flight.
        if (latestPermissionsRoleRef.current !== roleId) return;

        if (error) {
          captureException(
            new Error(error.message),
            'OrgCoreProvider.fetchPermissions',
            { roleId },
          );
          setPermissions(new Set());
          return;
        }

        type Row = {
          org_permissions: { key: string } | { key: string }[] | null;
        };
        const keys = new Set<string>();
        for (const row of (data ?? []) as Row[]) {
          const perm = row?.org_permissions;
          if (!perm) continue;
          if (Array.isArray(perm)) {
            for (const p of perm) if (p?.key) keys.add(p.key);
          } else if (perm.key) {
            keys.add(perm.key);
          }
        }
        setPermissions(keys);
      } catch (err) {
        if (latestPermissionsRoleRef.current !== roleId) return;
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'OrgCoreProvider.fetchPermissions',
          { roleId },
        );
        setPermissions(new Set());
      }
    },
    [],
  );

  /** Fetch members for an org, joined with role info. */
  const fetchOrgMembers = useCallback(async (orgId: string) => {
    latestMembersOrgRef.current = orgId;
    try {
      const { data, error } = await supabaseRef.current
        .from('org_members')
        .select('*, org_roles(*)')
        .eq('organization_id', orgId);

      // Drop a stale response: the active org changed mid-flight.
      if (latestMembersOrgRef.current !== orgId) return;

      if (error) {
        captureException(
          new Error(error.message),
          'OrgCoreProvider.fetchOrgMembers',
          { orgId },
        );
        setOrgMembers([]);
        return;
      }
      setOrgMembers((data ?? []) as OrgMember[]);
    } catch (err) {
      if (latestMembersOrgRef.current !== orgId) return;
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'OrgCoreProvider.fetchOrgMembers',
        { orgId },
      );
      setOrgMembers([]);
    }
  }, []);

  /** Re-fetch only the active org's member list. */
  const refreshMembers = useCallback(async () => {
    const orgId = activeOrg?.id;
    if (!orgId) return;
    await fetchOrgMembers(orgId);
  }, [activeOrg?.id, fetchOrgMembers]);

  /**
   * Update user metadata (first/last name). Captures to PostHog on failure
   * and re-throws so the caller can show UI state.
   */
  const updateProfile = useCallback(
    async (data: { firstName: string; lastName: string }) => {
      try {
        const { error: metaError } = await supabaseRef.current.auth.updateUser({
          data: { first_name: data.firstName, last_name: data.lastName },
        });
        if (metaError) {
          const err = new Error(metaError.message);
          captureException(err, 'OrgCoreProvider.updateProfile', {
            userId: user?.id,
          });
          throw err;
        }
      } catch (err) {
        if (!(err instanceof Error) || err.message === '') {
          captureException(
            err instanceof Error ? err : new Error(String(err)),
            'OrgCoreProvider.updateProfile',
            { userId: user?.id },
          );
        }
        throw err;
      }
    },
    [user?.id],
  );

  /**
   * Create a new organization. Sends X-Idempotency-Key (generated when not
   * provided) and name. On success, re-fetches memberships and sets the new
   * org as active.
   */
  const createOrg = useCallback(
    async (name: string, idempotencyKey?: string): Promise<Organization> => {
      if (creatingRef.current) {
        throw new CreateOrgError('Another organization is already being created');
      }
      creatingRef.current = true;

      const key =
        idempotencyKey ??
        (typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

      try {
        const {
          data: { session },
        } = await supabaseRef.current.auth.getSession();
        if (!session?.access_token) {
          const err = new CreateOrgError('Not authenticated');
          captureException(err, 'OrgCoreProvider.createOrg', { userId: user?.id });
          throw err;
        }

        let response: Response;
        try {
          response = await fetch('/api/billing/create-org', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
              'X-Idempotency-Key': key,
            },
            body: JSON.stringify({ name }),
          });
        } catch (fetchErr) {
          captureException(
            fetchErr instanceof Error ? fetchErr : new Error(String(fetchErr)),
            'OrgCoreProvider.createOrg',
            { userId: user?.id, idempotencyKey: key },
          );
          throw new CreateOrgError('Network error while creating organization');
        }

        if (!response.ok) {
          let errorCode: string | undefined;
          try {
            const body = (await response.clone().json()) as { error?: string };
            errorCode = body?.error;
          } catch {
            // Body isn't JSON, fall through to generic error.
          }

          if (response.status === 403 && errorCode === 'org_limit_reached') {
            const err = new OrgLimitReachedError();
            captureException(err, 'OrgCoreProvider.createOrg', {
              userId: user?.id,
              status: 403,
              idempotencyKey: key,
            });
            throw err;
          }

          if (
            response.status === 409 &&
            errorCode === 'create_failed_retry_with_new_key'
          ) {
            const err = new CreateOrgRetryError();
            captureException(err, 'OrgCoreProvider.createOrg', {
              userId: user?.id,
              status: 409,
              idempotencyKey: key,
            });
            throw err;
          }

          const err = new CreateOrgError(
            `Failed to create organization (${response.status})`,
          );
          captureException(err, 'OrgCoreProvider.createOrg', {
            userId: user?.id,
            status: response.status,
            errorCode: errorCode ?? null,
            idempotencyKey: key,
          });
          throw err;
        }

        // Re-fetch memberships (source of truth for the new org id).
        const { data: reFetched, error: fetchError } = await supabaseRef.current
          .from('org_members')
          .select('*, organizations(*), org_roles(*)')
          .eq('user_id', user!.id);

        if (fetchError) {
          const err = new CreateOrgError(fetchError.message);
          captureException(err, 'OrgCoreProvider.createOrg', {
            userId: user?.id,
            step: 'reFetchMemberships',
          });
          throw err;
        }

        const newMemberships = (reFetched ?? []) as UserOrgMembership[];
        if (newMemberships.length === 0) {
          const err = new CreateOrgError(
            'Organization created but membership not found',
          );
          captureException(err, 'OrgCoreProvider.createOrg', {
            userId: user?.id,
            step: 'reFetchMemberships',
          });
          throw err;
        }

        const prevIds = new Set(memberships.map((m) => m.organization_id));
        const newMembership =
          newMemberships.find((m) => !prevIds.has(m.organization_id)) ??
          newMemberships[newMemberships.length - 1];
        const newOrg = newMembership.organizations;

        setMemberships(newMemberships);
        setActiveOrgState(newOrg);
        try {
          localStorage.setItem(
            ACTIVE_ORG_STORAGE_KEY,
            newMembership.organization_id,
          );
        } catch {
          // localStorage unavailable; non-fatal
        }

        // Capture any trial info returned by the metering service via a
        // detached promise so the body-stream read doesn't sit between this
        // fetch and any subsequent awaited work. The setter fires BEFORE we
        // transition onboardingState so the SetupFlow reliably sees both
        // memberships populated AND justActivatedTrial set.
        let trialInfoPromise: Promise<void> | null = null;
        if (response.ok) {
          trialInfoPromise = response
            .clone()
            .json()
            .then((body: unknown) => {
              const trial =
                body && typeof body === 'object' && 'trial' in body && body.trial
                  ? (body.trial as {
                      subscription_id?: string | null;
                      trial_start?: number | null;
                      trial_end?: number | null;
                    })
                  : null;
              if (trial && typeof trial.subscription_id === 'string') {
                setJustActivatedTrial({
                  subscriptionId: trial.subscription_id,
                  trialStart: trial.trial_start ?? null,
                  trialEnd: trial.trial_end ?? null,
                });
              }
            })
            .catch(() => {
              // Non-JSON body, no trial info to capture.
            });
        }

        if (trialInfoPromise) {
          await trialInfoPromise;
        }

        setOnboardingState({ status: 'none' });

        // Strip ?onboarding param so the dev override doesn't re-trigger.
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          if (url.searchParams.has('onboarding')) {
            url.searchParams.delete('onboarding');
            window.history.replaceState({}, '', url.pathname + url.search);
          }
        }

        return newOrg;
      } finally {
        creatingRef.current = false;
      }
    },
    [user?.id, memberships],
  );

  /**
   * Create an org plus its default workspace via the metering proxy. See the
   * legacy provider for the full contract; behaviour is preserved verbatim.
   */
  const createOrgWithWorkspace = useCallback(
    async (
      orgName: string,
      workspaceName: string,
      websiteUrl: string,
      idempotencyKey: string,
    ): Promise<{ orgId: string; workspaceId: string }> => {
      try {
        const {
          data: { session },
        } = await supabaseRef.current.auth.getSession();
        if (!session?.access_token) {
          const err = new CreateOrgError('Not authenticated');
          captureException(err, 'OrgCoreProvider.createOrgWithWorkspace', {
            userId: user?.id,
            idempotencyKey,
          });
          throw err;
        }

        let response: Response;
        try {
          response = await fetch('/api/billing/create-org', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
              'X-Idempotency-Key': idempotencyKey,
            },
            body: JSON.stringify({
              name: orgName,
              workspace_name: workspaceName,
              website_url: websiteUrl,
            }),
          });
        } catch (fetchErr) {
          captureException(
            fetchErr instanceof Error ? fetchErr : new Error(String(fetchErr)),
            'OrgCoreProvider.createOrgWithWorkspace',
            { userId: user?.id, idempotencyKey },
          );
          throw new CreateOrgError('Network error while creating organization');
        }

        if (!response.ok) {
          let errorCode: string | undefined;
          try {
            const body = (await response.clone().json()) as { error?: string };
            errorCode = body?.error;
          } catch {
            // Body isn't JSON, fall through to generic error.
          }

          if (response.status === 403 && errorCode === 'org_limit_reached') {
            const err = new OrgLimitReachedError();
            captureException(err, 'OrgCoreProvider.createOrgWithWorkspace', {
              userId: user?.id,
              status: 403,
              idempotencyKey,
            });
            throw err;
          }

          if (
            response.status === 409 &&
            errorCode === 'create_failed_retry_with_new_key'
          ) {
            const err = new CreateOrgRetryError();
            captureException(err, 'OrgCoreProvider.createOrgWithWorkspace', {
              userId: user?.id,
              status: 409,
              idempotencyKey,
            });
            throw err;
          }

          const err = new CreateOrgError(
            `Failed to create organization (${response.status})`,
          );
          captureException(err, 'OrgCoreProvider.createOrgWithWorkspace', {
            userId: user?.id,
            status: response.status,
            errorCode: errorCode ?? null,
            idempotencyKey,
          });
          throw err;
        }

        let body: {
          organization_id?: string;
          brand_id?: string;
          trial?: {
            subscription_id?: string | null;
            trial_start?: number | null;
            trial_end?: number | null;
          } | null;
        } = {};
        try {
          body = (await response.clone().json()) as typeof body;
        } catch {
          const err = new CreateOrgError(
            'Metering service returned a non-JSON response',
          );
          captureException(err, 'OrgCoreProvider.createOrgWithWorkspace', {
            userId: user?.id,
            idempotencyKey,
          });
          throw err;
        }

        const orgId = body.organization_id;
        const workspaceId = body.brand_id;
        if (!orgId || !workspaceId) {
          const err = new CreateOrgError(
            'Metering service returned an incomplete response',
          );
          captureException(err, 'OrgCoreProvider.createOrgWithWorkspace', {
            userId: user?.id,
            idempotencyKey,
          });
          throw err;
        }

        const { data: reFetched, error: fetchError } = await supabaseRef.current
          .from('org_members')
          .select('*, organizations(*), org_roles(*)')
          .eq('user_id', user!.id);

        if (fetchError) {
          const err = new CreateOrgError(fetchError.message);
          captureException(err, 'OrgCoreProvider.createOrgWithWorkspace', {
            userId: user?.id,
            step: 'reFetchMemberships',
            idempotencyKey,
          });
          throw err;
        }

        const newMemberships = (reFetched ?? []) as UserOrgMembership[];
        setMemberships(newMemberships);

        const newMembership = newMemberships.find(
          (m) => m.organization_id === orgId,
        );
        if (newMembership) {
          setActiveOrgState(newMembership.organizations);
          try {
            localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
          } catch {
            // localStorage unavailable; non-fatal
          }
        }

        const trial = body.trial ?? null;
        if (trial && typeof trial.subscription_id === 'string') {
          setJustActivatedTrial({
            subscriptionId: trial.subscription_id,
            trialStart: trial.trial_start ?? null,
            trialEnd: trial.trial_end ?? null,
          });
        }

        setOnboardingState({ status: 'none' });

        return { orgId, workspaceId };
      } catch (err) {
        if (
          err instanceof OrgLimitReachedError ||
          err instanceof CreateOrgRetryError ||
          err instanceof CreateOrgError
        ) {
          throw err;
        }
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'OrgCoreProvider.createOrgWithWorkspace',
          { userId: user?.id, idempotencyKey },
        );
        throw new CreateOrgError(
          err instanceof Error ? err.message : 'Failed to create organization',
        );
      }
    },
    [user?.id],
  );

  /**
   * Complete the onboarding flow: update user profile and create org.
   * Delegates to updateProfile + createOrg so the logic lives in one place.
   */
  const completeOnboarding = useCallback(
    async (data: { firstName: string; lastName: string; orgName: string }) => {
      // BLOCKING STEP 1: Update profile.
      await updateProfile({ firstName: data.firstName, lastName: data.lastName });

      // PRE-FLIGHT: If membership already exists (retry after partial
      // success), skip org creation.
      const { data: preCheck, error: preCheckError } = await supabaseRef.current
        .from('org_members')
        .select('*, organizations(*), org_roles(*)')
        .eq('user_id', user!.id);

      if (preCheckError) {
        const err = new Error(
          'Unable to verify organization status. Please try again.',
        );
        captureException(
          new Error(preCheckError.message),
          'OrgCoreProvider.completeOnboarding',
          { userId: user?.id, step: 'preFlightCheck' },
        );
        throw err;
      }

      const existing = (preCheck ?? []) as UserOrgMembership[];
      if (existing.length > 0) {
        const first = existing[0];
        setMemberships(existing);
        setActiveOrgState(first.organizations);
        try {
          localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, first.organization_id);
        } catch {
          // localStorage unavailable
        }
        setOnboardingState({ status: 'none' });
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          if (url.searchParams.has('onboarding')) {
            url.searchParams.delete('onboarding');
            window.history.replaceState({}, '', url.pathname + url.search);
          }
        }
        return;
      }

      // BLOCKING STEP 2 + 3: Create org + refetch memberships.
      await createOrg(data.orgName);
    },
    [updateProfile, createOrg, user?.id],
  );

  /** Main initialization: fetch memberships, auto-create if needed, set active org. */
  useEffect(() => {
    if (authLoading || !user?.id) {
      setIsLoading(!authLoading);
      if (!authLoading && !user?.id) {
        // User signed out: clear all org state so the next login starts clean.
        setMemberships([]);
        setActiveOrgState(null);
        setOrgMembers([]);
        setPermissions(new Set());
        setOrgError(null);
      }
      return undefined;
    }

    let cancelled = false;

    const init = async () => {
      setIsLoading(true);
      setOrgError(null);

      try {
        const { data: memberData, error: memberError } = await supabaseRef.current
          .from('org_members')
          .select('*, organizations(*), org_roles(*)')
          .eq('user_id', user.id);

        if (memberError) {
          captureException(
            new Error(memberError.message),
            'OrgCoreProvider.fetchMemberships',
            { userId: user.id },
          );
          if (!cancelled) setIsLoading(false);
          return;
        }

        const finalMemberships = (memberData ?? []) as UserOrgMembership[];

        if (finalMemberships.length === 0) {
          if (!cancelled) {
            setMemberships([]);
            setActiveOrgState(null);
            setOrgMembers([]);
            setOnboardingState({ status: 'create-org' });
            setIsLoading(false);
          }
          return;
        }

        if (cancelled) return;

        setMemberships(finalMemberships);
        setOnboardingState({ status: 'none' });

        let selectedOrg: Organization | null = null;

        if (finalMemberships.length === 1) {
          selectedOrg = finalMemberships[0].organizations;
        } else if (finalMemberships.length > 1) {
          let storedOrgId: string | null = null;
          try {
            storedOrgId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
          } catch {
            // localStorage unavailable
          }

          const stored = storedOrgId
            ? finalMemberships.find((m) => m.organization_id === storedOrgId)
            : null;
          selectedOrg = stored
            ? stored.organizations
            : finalMemberships[0].organizations;
        }

        if (cancelled) return;

        setActiveOrgState(selectedOrg);
      } catch (err) {
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'OrgCoreProvider.init',
          { userId: user.id },
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void init();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading, refreshKey]);

  // DEV: Force onboarding splash with ?onboarding=true for testing.
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !isLoading &&
      onboardingState.status === 'none' &&
      new URLSearchParams(window.location.search).get('onboarding') === 'true'
    ) {
      setOnboardingState({ status: 'create-org' });
    }
  }, [isLoading, onboardingState.status]);

  // Re-fetch members when the active org changes.
  useEffect(() => {
    if (activeOrg?.id) {
      void fetchOrgMembers(activeOrg.id);
    }
  }, [activeOrg?.id, fetchOrgMembers]);

  // Re-fetch permissions whenever the active role changes. Keyed on role_id
  // (not activeOrg.id) so a role transfer inside the same org also refreshes.
  useEffect(() => {
    void fetchPermissions(activeRoleId);
  }, [activeRoleId, fetchPermissions]);

  /** Stable hasPermission helper bound to the current permission set. */
  const hasPermission = useCallback(
    (key: string) => permissions.has(key),
    [permissions],
  );

  /** Clear the just-activated trial flag (core half of acknowledgeTrial). */
  const clearJustActivatedTrial = useCallback(() => {
    setJustActivatedTrial(null);
  }, []);

  /** Derive setup steps from user metadata + memberships + trial state. */
  const setupSteps = useMemo<SetupStep[]>(() => {
    const steps: SetupStep[] = [];
    const firstName = (
      user?.user_metadata as { first_name?: string } | undefined
    )?.first_name;
    if (!firstName) steps.push('profile');
    if (memberships.length === 0) steps.push('create-org');
    if (justActivatedTrial && memberships.length > 0)
      steps.push('trial-activated');
    return steps;
  }, [user?.user_metadata, memberships, justActivatedTrial]);

  const value = useMemo<OrgCoreContextType>(
    () => ({
      activeOrg,
      setActiveOrg,
      userOrgs,
      memberships,
      orgMembers,
      userRole,
      permissions,
      hasPermission,
      isLoading,
      orgError,
      refreshCore,
      refreshMembers,
      onboardingState,
      setupSteps,
      updateProfile,
      createOrg,
      createOrgWithWorkspace,
      completeOnboarding,
      justActivatedTrial,
      clearJustActivatedTrial,
    }),
    [
      activeOrg,
      setActiveOrg,
      userOrgs,
      memberships,
      orgMembers,
      userRole,
      permissions,
      hasPermission,
      isLoading,
      orgError,
      refreshCore,
      refreshMembers,
      onboardingState,
      setupSteps,
      updateProfile,
      createOrg,
      createOrgWithWorkspace,
      completeOnboarding,
      justActivatedTrial,
      clearJustActivatedTrial,
    ],
  );

  return (
    <OrgCoreContext.Provider value={value}>{children}</OrgCoreContext.Provider>
  );
};

/**
 * Access the core organization context.
 *
 * @throws Error if called outside `OrgCoreProvider`.
 */
export const useOrgCore = (): OrgCoreContextType => {
  const context = useContext(OrgCoreContext);
  if (context === undefined) {
    throw new Error('useOrgCore must be used within an OrgCoreProvider');
  }
  return context;
};

export default OrgCoreProvider;
